import { AnalyticsEvent, AnalyticsManager } from '@oy/analytics';
import { LEGAL_DOC_URL } from '@oy/types';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { signLegalDocument } from 'src/clients';
import { useTranslate } from 'src/hooks';
import { useLegalDocumentData } from 'src/hooks/data/useLegalDocumentData';
import { RootState } from 'src/redux/reducers/rootReducer';
import { ACTION_TYPES } from 'src/redux/types/signLegalDocumentType';
import useSWRMutation from 'swr/mutation';
import { useSignLegalDocumentByIdData } from './hooks/useSignLegalDocumentByIdData';

type LegalDocumentDataType = {
  id: string;
  file_name: string;
  type: string;
  status: string;
  product?: string;
  created: string;
};

type FetcherType = {
  arg: string[];
};

export const useSignLegalDocumentModalImpl = (resetPosition: () => void) => {
  const { data: legalDocs, isShow, isSigned, mutate } = useLegalDocumentData();

  const [activeIndex, setActiveIndex] = useState(0);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isCurrentDocsSigned, setIsCurrentDocsSigned] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const { getTranslation, lang } = useTranslate();
  const dispatch = useDispatch();
  const { showModal: show } = useSelector(
    (state: RootState) => state.signLegalDocument,
  );

  const {
    data: currentFile,
    isLoading,
    error,
  } = useSignLegalDocumentByIdData(legalDocs, activeIndex);
  const openModal = useCallback(() => {
    dispatch({ type: ACTION_TYPES.SHOW_MODAL });
  }, [dispatch]);

  const closeModal = () => {
    dispatch({ type: ACTION_TYPES.CLOSE_MODAL });
  };

  const putSignLegalDocument = async (_url: string, options: FetcherType) => {
    closeModal();
    const res = await signLegalDocument(options.arg);
    return res.data?.success;
  };

  const { trigger, isMutating } = useSWRMutation(
    `${LEGAL_DOC_URL}/sign`,
    putSignLegalDocument,
    {
      onSuccess: () => {
        mutate();
        setShowSuccessModal(true);
      },
      onError: () => {
        openModal();
      },
    },
  );

  const onCloseModal = () => {
    AnalyticsManager.instance().track(AnalyticsEvent.click_close_pkspopup, {
      lang: lang,
      platform: 'desktop',
      doctype: legalDocs.some(item => item.type === 'CUSTOM_AGREEMENT')
        ? 'suratketerangan'
        : 'pks',
    });
    closeModal();
    setShowCloseModal(true);
  };

  const onCloseModalConfirmation = () => {
    setShowCloseModal(false);
  };

  const onCancelCloseModalConfirmation = () => {
    setShowCloseModal(false);
    openModal();
  };

  const onSubmit = () => {
    AnalyticsManager.instance().track(AnalyticsEvent.click_submit_pks, {
      lang: lang,
      platform: 'desktop',
      doctype: legalDocs.some(item => item.type === 'CUSTOM_AGREEMENT')
        ? 'suratketerangan'
        : 'pks',
    });
    const documents = legalDocs.map(document => document.id);
    trigger(documents);
  };

  const onChecked = () => {
    setIsCurrentDocsSigned(prevState => !prevState);
  };

  const onNextDocument = () => {
    resetPosition();
    setIsCurrentDocsSigned(false);
    setActiveIndex(prevIndex => prevIndex + 1);
  };

  const onLoadSuccess = (newNumPages: number) => {
    setNumPages(newNumPages);
  };

  const getTitleAppendix = (product: string) => {
    switch (product) {
      case 'DISBURSEMENT':
        return 'Disbursement';
      case 'PAYMENT_AGGREGATOR':
        return 'Payment Aggregator';
      case 'PAYMENT_LINK':
        return 'Payment Link';
      case 'VCC':
        return 'VCC';
      default:
        return 'Unknown';
    }
  };

  const getStepTitle = (document: LegalDocumentDataType) => {
    switch (document.type) {
      case 'DOCUMENT_DEFICIENCY':
        return 'Document Deficiency';
      case 'SERVICE_AGREEMENT':
        return getTranslation('common_service_aggrement');
      case 'TERMS_CONDITIONS':
        return getTranslation('common_terms_conditions');
      case 'CUSTOM_AGREEMENT':
        return getTranslation('common_service_aggrement');
      case 'APPENDIX':
        return `${getTranslation('common_appendix')} - ${getTitleAppendix(
          document.product ?? '',
        )}`;
      default:
        return 'Unknown Document';
    }
  };

  const onCloseModalSuccess = () => {
    setShowSuccessModal(false);
  };

  const state = {
    activeIndex,
    show: show && isShow,
    isLoading,
    isCurrentDocsSigned,
    currentFile,
    isError: !!error,
    numPages,
    legalDocs,
    showCloseModal,
    showSuccessModal,
    isSubmitting: isMutating,
    isAlreadySigned: show && isSigned,
  };
  return {
    state,
    onCloseModal,
    onSubmit,
    onNextDocument,
    onLoadSuccess,
    onChecked,
    getStepTitle,
    onCloseModalConfirmation,
    onCancelCloseModalConfirmation,
    onCloseModalSuccess,
    onCloseAlreadySigned: closeModal,
  };
};
